import { useRef, useState, type FormEvent } from "react";
import {
  useCreateProvider,
  useCreateProviderLogin,
  useDeleteProvider,
  useUpdateProvider,
  useUploadArtwork,
  QUOTE_TEMPLATES,
  type Provider,
  type ProviderIn,
  type QuoteTemplate,
} from "../../api/admin";

const TEXT_FIELDS: [keyof ProviderIn, string][] = [
  ["contact_person", "Contact person"],
  ["contact_number", "Contact number"],
  ["email", "Contact email"],
  ["address_line_1", "Address line 1"],
  ["address_line_2", "Address line 2"],
  ["address_line_3", "Address line 3"],
  ["address_line_4", "Address line 4"],
  ["registration_no", "Registration no."],
  ["vat_no", "VAT no."],
  ["agent_line", "Agent line (letterhead)"],
  ["legal_name", "Registered name (letterhead)"],
  ["fax_number", "Fax number"],
  // Not "account no.": that is ours for them, a field above.
  ["customer_account_no", "Our customer number with them"],
  ["labour_rate", "Labour rate (R)"],
  ["labour_description", "Labour description"],
  ["km_rate", "Per-kilometre rate (R)"],
];

function blank(p?: Provider): ProviderIn {
  return {
    name: p?.name ?? "",
    contact_person: p?.contact_person ?? "",
    contact_number: p?.contact_number ?? "",
    email: p?.email ?? "",
    address_line_1: p?.address_line_1 ?? "",
    address_line_2: p?.address_line_2 ?? "",
    address_line_3: p?.address_line_3 ?? "",
    address_line_4: p?.address_line_4 ?? "",
    vatable: p?.vatable ?? false,
    active: p?.active ?? true,
    registration_no: p?.registration_no ?? "",
    vat_no: p?.vat_no ?? "",
    agent_line: p?.agent_line ?? "",
    quote_template: p?.quote_template ?? "seapro",
    legal_name: p?.legal_name ?? "",
    fax_number: p?.fax_number ?? "",
    customer_account_no: p?.customer_account_no ?? "",
    labour_rate: p?.labour_rate ?? "",
    labour_description: p?.labour_description ?? "",
    km_rate: p?.km_rate ?? "",
  };
}

/** Blank text is "not set", sent as null, so clearing a rate clears it
 * rather than saving an empty string the server would refuse. */
function clean(form: ProviderIn): ProviderIn {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(form)) {
    out[key] = typeof value === "string" ? value.trim() || null : value;
  }
  return { ...(out as ProviderIn), name: form.name.trim() };
}

function ProviderFields({
  form,
  set,
  disabled,
}: {
  form: ProviderIn;
  set: (k: keyof ProviderIn, v: string | boolean) => void;
  disabled: boolean;
}) {
  return (
    <div className="admin-form-grid">
      <label className="field">
        Name
        <input value={form.name} onChange={(e) => set("name", e.target.value)} required disabled={disabled} />
      </label>
      <label className="field">
        Quotation template
        <select
          value={form.quote_template ?? "seapro"}
          onChange={(e) => set("quote_template", e.target.value as QuoteTemplate)}
          disabled={disabled}
        >
          {QUOTE_TEMPLATES.map((template) => (
            <option key={template.value} value={template.value}>
              {template.label}
            </option>
          ))}
        </select>
      </label>
      {TEXT_FIELDS.map(([key, label]) => (
        <label key={key} className="field">
          {label}
          <input value={(form[key] as string | null) ?? ""} onChange={(e) => set(key, e.target.value)} disabled={disabled} />
        </label>
      ))}
      <label className="check">
        <input type="checkbox" checked={form.vatable ?? false} onChange={(e) => set("vatable", e.target.checked)} disabled={disabled} />
        VAT registered
      </label>
      <label className="check">
        <input type="checkbox" checked={form.active ?? true} onChange={(e) => set("active", e.target.checked)} disabled={disabled} />
        Active
      </label>
    </div>
  );
}

/** A new provider, and optionally the crew's shared login in the same step. */
export function CreateProvider({ onDone }: { onDone: () => void }) {
  const create = useCreateProvider();
  const [form, setForm] = useState<ProviderIn>(blank());
  const [accNo, setAccNo] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const set = (k: keyof ProviderIn, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));
  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate(
      { ...clean(form), acc_no: accNo.trim(), login_email: loginEmail.trim() || null, login_password: loginPassword || null },
      { onSuccess: onDone },
    );
  }
  return (
    <form className="admin-form" onSubmit={submit}>
      <label className="field">
        Account no.
        <input value={accNo} onChange={(e) => setAccNo(e.target.value)} required />
      </label>
      <ProviderFields form={form} set={set} disabled={create.isPending} />
      <h3 className="section-label">Crew login (optional, both or neither)</h3>
      <div className="admin-form-grid">
        <label className="field">
          Email
          <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} />
        </label>
        <label className="field">
          Password
          <input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} />
        </label>
      </div>
      {create.isError ? <p className="notice bad">{create.error.message}</p> : null}
      <div className="admin-form-actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={create.isPending || accNo.trim() === "" || form.name.trim() === ""}
        >
          Add
        </button>
      </div>
    </form>
  );
}

function ArtworkSection({ provider }: { provider: Provider }) {
  const artwork = useUploadArtwork();
  const [kind, setKind] = useState<"logo" | "agent_mark">("logo");
  const input = useRef<HTMLInputElement>(null);
  return (
    <section className="admin-form">
      <h3 className="section-label">Artwork</h3>
      {provider.logo_url || provider.agent_mark_url ? (
        <div className="photo-grid">
          {provider.logo_url ? (
            <figure>
              <img src={provider.logo_url} alt="Logo" />
              <figcaption>Logo</figcaption>
            </figure>
          ) : null}
          {provider.agent_mark_url ? (
            <figure>
              <img src={provider.agent_mark_url} alt="Agent mark" />
              <figcaption>Agent mark</figcaption>
            </figure>
          ) : null}
        </div>
      ) : (
        <p className="muted-note">No artwork yet: a quotation prints without a logo.</p>
      )}
      <div className="toolbar">
        <select className="select" aria-label="Which artwork" value={kind} onChange={(e) => setKind(e.target.value as "logo" | "agent_mark")}>
          <option value="logo">Logo</option>
          <option value="agent_mark">Agent mark</option>
        </select>
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              artwork.mutate({ id: provider.id, kind, file });
            }
            e.target.value = "";
          }}
        />
        <button type="button" className="btn" disabled={artwork.isPending} onClick={() => input.current?.click()}>
          Upload PNG or JPEG
        </button>
      </div>
      {artwork.isError ? <p className="notice bad">{artwork.error.message}</p> : null}
    </section>
  );
}

function CrewLoginSection({ provider }: { provider: Provider }) {
  const login = useCreateProviderLogin();
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  if (provider.login !== null) {
    return (
      <p className="muted-note">
        Field operator login: {provider.login.email}
        {provider.login.active ? "" : " (deactivated)"}. Manage it under Users.
      </p>
    );
  }
  return (
    <section className="admin-form">
      <h3 className="section-label">Crew login</h3>
      <form
        className="admin-form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          login.mutate({ id: provider.id, login_email: loginEmail.trim(), login_password: loginPassword });
        }}
      >
        <label className="field">
          Email
          <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required />
        </label>
        <label className="field">
          Password
          <input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required />
        </label>
        <div className="admin-form-actions">
          <button type="submit" className="btn" disabled={login.isPending}>
            Create login
          </button>
        </div>
      </form>
      {login.isError ? <p className="notice bad">{login.error.message}</p> : null}
    </section>
  );
}

/** Everything about one provider: its letterhead and rates, its artwork and
 * its crew login. Delete is refused by the server once anything names it. */
export function EditProvider({ provider, onDone }: { provider: Provider; onDone: () => void }) {
  const update = useUpdateProvider();
  const remove = useDeleteProvider();
  const [form, setForm] = useState<ProviderIn>(blank(provider));
  const set = (k: keyof ProviderIn, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <>
      <form
        className="admin-form"
        onSubmit={(e) => {
          e.preventDefault();
          update.mutate({ id: provider.id, body: clean(form) });
        }}
      >
        <ProviderFields form={form} set={set} disabled={update.isPending} />
        {update.isError ? <p className="notice bad">{update.error.message}</p> : null}
        {update.isSuccess ? <p className="notice ok">Saved.</p> : null}
        <div className="admin-form-actions">
          <button
            type="button"
            className="btn btn-danger"
            disabled={remove.isPending}
            onClick={() => remove.mutate(provider.id, { onSuccess: onDone })}
          >
            Delete
          </button>
          <button type="submit" className="btn btn-primary" disabled={update.isPending}>
            Save
          </button>
        </div>
        {remove.isError ? <p className="notice bad">{remove.error.message}</p> : null}
      </form>
      <ArtworkSection provider={provider} />
      <CrewLoginSection provider={provider} />
    </>
  );
}
